// IOS "show" output renderers. Everything is generated from live device state,
// so output always reflects what the learner (or an injected fault) configured.

import { secGlobalLines, secIfLines, secTailLines, secNumberedAclLines, secLineBlocks } from './secCli.js';
import { ifStatus, linkOf, peerOf, routingTable, dev, isHost } from '../net/sim.js';
import { ipToInt, macCisco, maskToPrefix, classfulPrefix, networkOf, prefixToMask, v6Compress } from '../net/addr.js';
import { shortIf, cdpIf, ifSort } from './ifnames.js';
import { mgmtGlobalLines, mgmtTailLines, mgmtIfLines } from './mgmtCli.js';
import { cdpOn } from '../net/system.js';
import { switchIfLines, switchGlobalLines } from './switching.js';
import { routingConfigLines, routingIfLines } from './routingCli.js';
import { serialInterface, tunnelInterface, wanConfigLines, wanIfLines, isSerial, isTunnel } from './wanCli.js';
import { ifLoad, pktsFor } from '../net/qos.js';
import { svcConfigLines, svcTailLines, svcIfLines } from './servicesCli.js';
import { isSvi, isPo, isSubif, channelMembers, memberFlag } from '../net/l2.js';

const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);
const sortedIfs = (d) => Object.keys(d.interfaces).sort(ifSort);

export function ipIntBrief(net, id) {
  const d = dev(net, id);
  const rows = [`${pad('Interface', 23)}${pad('IP-Address', 16)}OK? Method ${pad('Status', 22)}Protocol`];
  for (const n of sortedIfs(d)) {
    const i = d.interfaces[n];
    const st = ifStatus(net, id, n);
    const ip = i.ip ? i.ip.ip : 'unassigned';
    const method = i.ip ? 'manual' : 'unset ';
    rows.push(`${pad(n, 23)}${pad(ip, 16)}YES ${pad(method, 7)}${pad(st.status, 22)}${st.protocol}`);
  }
  if (d.kind === 'switch' && !d.interfaces.Vlan1) rows.push(`${pad('Vlan1', 23)}${pad('unassigned', 16)}YES ${pad('unset', 7)}${pad('administratively down', 22)}down`);
  return rows;
}

export function interfacesStatus(net, id) {
  const d = dev(net, id);
  const rows = [`${pad('Port', 10)}${pad('Name', 19)}${pad('Status', 13)}${pad('Vlan', 11)}${pad('Duplex', 7)}${lpad('Speed', 6)} Type`];
  for (const n of sortedIfs(d).filter((x) => !isSvi(x))) {
    const i = d.interfaces[n];
    const st = ifStatus(net, id, n);
    // an access port whose VLAN is not in the VLAN database forwards nothing: IOS shows it "inactive"
    const noVlan = d.kind === 'switch' && i.mode !== 'trunk' && !i.channelGroup && !d.vlans?.[i.accessVlan];
    const status = i.shutdown ? 'disabled' : i.errDisabled ? 'err-disabled' : st.suspended ? 'suspnd' : st.connected ? (noVlan ? 'inactive' : 'connected') : 'notconnect';
    const vlan = i.mode === 'trunk' ? 'trunk' : String(i.accessVlan);
    const gig = n.startsWith('Gig') || (isPo(n) && channelMembers(net, id, n).some((m) => m.ifc.startsWith('Gig')));
    const duplex = st.connected ? (i.duplex === 'auto' ? (st.errors ? 'a-half' : 'a-full') : i.duplex) : 'auto';
    const speed = st.connected ? (i.speed === 'auto' ? (gig ? 'a-1000' : 'a-100') : i.speed) : 'auto';
    rows.push(`${pad(shortIf(n), 10)}${pad(i.description.slice(0, 18), 19)}${pad(status, 13)}${pad(vlan, 11)}${pad(duplex, 7)}${lpad(speed, 6)} ${isPo(n) ? '' : gig ? '10/100/1000BaseTX' : '10/100BaseTX'}`.trimEnd());
  }
  return rows;
}

export function showInterface(net, id, n) {
  if (isSerial(n)) return serialInterface(net, id, n);
  if (isTunnel(n)) return tunnelInterface(net, id, n);
  const d = dev(net, id);
  const ld = d.kind === 'router' ? ifLoad(net, id, n) : null;
  const i = d.interfaces[n];
  const st = ifStatus(net, id, n);
  const gig = n.startsWith('Gig');
  const head = st.status === 'administratively down'
    ? `${n} is administratively down, line protocol is down${isSvi(n) ? '' : ' (disabled)'}`
    : `${n} is ${st.status}, line protocol is ${st.protocol}${st.errDisabled ? ' (err-disabled)' : st.suspended ? ' (suspended)' : st.protocol === 'down' && d.kind === 'switch' && !isSvi(n) ? ' (notconnect)' : ''}`;
  const hw = isSvi(n) ? 'EtherSVI' : isPo(n) ? 'EtherChannel' : d.kind === 'router' ? 'CN Gigabit Ethernet' : gig ? 'Gigabit Ethernet' : 'Fast Ethernet';
  const c = i.counters;
  const out = [head, `  Hardware is ${hw}, address is ${macCisco(i.mac)} (bia ${macCisco(i.mac)})`];
  if (i.description) out.push(`  Description: ${i.description}`);
  if (i.encap) out.push(`  Encapsulation 802.1Q Virtual LAN, Vlan ID  ${i.encap.vlan}.${i.encap.native ? ' (native)' : ''}`);
  if (i.ip) out.push(`  Internet address is ${i.ip.ip}/${maskToPrefix(i.ip.mask)}`);
  if (isPo(n)) {
    const mem = channelMembers(net, id, n).filter((m) => m.flag === 'P');
    out.push(`  Members in this channel: ${mem.map((m) => shortIf(m.ifc)).join(' ') || '(none)'}`);
  }
  out.push(
    `  MTU 1500 bytes, BW ${gig ? '1000000' : '100000'} Kbit/sec, DLY ${gig ? 10 : 100} usec,`,
    `     reliability ${st.errors ? '188' : '255'}/255, txload ${ld ? ld.txload : 1}/255, rxload ${ld ? ld.rxload : 1}/255`,
    '  Encapsulation ARPA, loopback not set',
    '  Keepalive set (10 sec)',
  );
  if (st.connected) out.push(`  ${st.errors ? 'Half' : 'Full'}-duplex, ${gig ? '1000Mb/s' : '100Mb/s'}, media type is 10/100${gig ? '/1000' : ''}BaseTX`);
  else out.push('  Auto-duplex, Auto-speed, media type is 10/100BaseTX');
  out.push(
    '  input flow-control is off, output flow-control is unsupported',
    '  ARP type: ARPA, ARP Timeout 04:00:00',
    `  Last input ${st.connected ? '00:00:01' : 'never'}, output ${st.connected ? '00:00:00' : 'never'}, output hang never`,
    '  Last clearing of "show interface" counters never',
    `  Input queue: 0/75/0/0 (size/max/drops/flushes); Total output drops: ${ld ? pktsFor(ld.drops) : 0}`,
    `  Queueing strategy: ${i.servicePolicy?.output ? 'Class-based queueing' : 'fifo'}`,
    `  Output queue: ${ld?.congested ? 40 : 0}/40 (size/max)`,
    `  5 minute input rate ${ld ? Math.round(ld.rxKbps * 1000) : st.connected ? '1000' : '0'} bits/sec, ${ld ? Math.round(ld.rxKbps / 2) : st.connected ? '1' : '0'} packets/sec`,
    `  5 minute output rate ${ld ? Math.round(ld.txKbps * 1000) : st.connected ? '1000' : '0'} bits/sec, ${ld ? Math.round(ld.txKbps / 2) : st.connected ? '1' : '0'} packets/sec`,
    `     ${c.inPkts} packets input, ${c.inPkts * 98} bytes, 0 no buffer`,
    `     Received ${Math.floor(c.inPkts / 3)} broadcasts (0 multicasts)`,
    `     ${st.errors ? 12 : 0} runts, 0 giants, 0 throttles`,
    `     ${c.inErrors} input errors, ${c.crc} CRC, ${Math.floor(c.crc / 4)} frame, 0 overrun, 0 ignored`,
    '     0 watchdog, 0 multicast, 0 pause input',
    `     ${c.outPkts} packets output, ${c.outPkts * 98} bytes, 0 underruns`,
    `     0 output errors, ${st.errors ? 9 : 0} collisions, ${st.connected ? 1 : 0} interface resets`,
    `     0 unknown protocol drops`,
    `     0 babbles, ${st.errors ? 7 : 0} late collision, 0 deferred`,
    '     0 lost carrier, 0 no carrier, 0 pause output',
    '     0 output buffer failures, 0 output buffers swapped out',
  );
  return out;
}

const RESERVED_VLANS = [[1002, 'fddi-default'], [1003, 'token-ring-default'], [1004, 'fddinet-default'], [1005, 'trnet-default']];
export function vlanBrief(net, id) {
  const d = dev(net, id);
  const out = [
    'VLAN Name                             Status    Ports',
    '---- -------------------------------- --------- -------------------------------',
  ];
  const ids = Object.keys(d.vlans).map(Number).sort((a, b) => a - b);
  for (const v of ids) {
    const ports = sortedIfs(d).filter((n) => !isSvi(n) && !d.interfaces[n].channelGroup && d.interfaces[n].mode !== 'trunk' && d.interfaces[n].accessVlan === v).map(shortIf);
    const chunks = [];
    for (let k = 0; k < ports.length; k += 4) chunks.push(ports.slice(k, k + 4).join(', '));
    const name = d.vlans[v].name;
    out.push(`${pad(v, 5)}${pad(name, 33)}${pad('active', 10)}${chunks[0] || ''}`.trimEnd());
    for (const c of chunks.slice(1)) out.push(`${' '.repeat(48)}${c}`);
  }
  for (const [v, n] of RESERVED_VLANS) out.push(`${pad(v, 5)}${pad(n, 33)}act/unsup`);
  return out;
}

export function macTable(net, id, filter = {}) {
  const d = dev(net, id);
  let rows = d.macTable.slice();
  if (filter.iface) rows = rows.filter((r) => r.port === filter.iface);
  if (filter.vlan) rows = rows.filter((r) => r.vlan === filter.vlan);
  rows.sort((a, b) => a.vlan - b.vlan || ifSort(a.port, b.port));
  const out = [
    '          Mac Address Table',
    '-------------------------------------------',
    '',
    'Vlan    Mac Address       Type        Ports',
    '----    -----------       --------    -----',
  ];
  if (!filter.dynamicOnly) out.push(' All    0100.0ccc.cccc    STATIC      CPU', ' All    ffff.ffff.ffff    STATIC      CPU');
  for (const r of rows) out.push(`${lpad(r.vlan, 4)}    ${macCisco(r.mac)}    DYNAMIC     ${shortIf(r.port)}`);
  out.push(`Total Mac Addresses for this criterion: ${rows.length + (filter.dynamicOnly ? 0 : 2)}`);
  return out;
}

export function ipRoute(net, id, keep = null) {
  const all = routingTable(net, id);
  const rt = keep ? all.filter(keep) : all;
  const out = [
    'Codes: L - local, C - connected, S - static, R - RIP, M - mobile, B - BGP',
    '       D - EIGRP, EX - EIGRP external, O - OSPF, IA - OSPF inter area',
    '       N1 - OSPF NSSA external type 1, N2 - OSPF NSSA external type 2',
    '       E1 - OSPF external type 1, E2 - OSPF external type 2',
    '       i - IS-IS, su - IS-IS summary, L1 - IS-IS level-1, L2 - IS-IS level-2',
    '       ia - IS-IS inter area, * - candidate default, U - per-user static route',
    '       o - ODR, P - periodic downloaded static route, + - replicated route',
    '',
  ];
  const def = all.find((r) => r.prefix === 0);
  out.push(def ? `Gateway of last resort is ${def.nh || '0.0.0.0'} to network 0.0.0.0` : 'Gateway of last resort is not set', '');
  const age = (nh) => { const n = nh.split('.').reduce((a, b) => a * 7 + +b, 0) % 3000; return `00:${String(8 + (n % 50)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`; };
  const tail = (r) => (r.src === 'static' ? `[${r.ad}/${r.metric}] via ${r.nh}` : `[${r.ad}/${r.metric}] via ${r.nh}, ${age(r.nh)}, ${r.ifc}`);
  const line = (r, indent) => {
    const code = pad(r.code, indent ? 9 : 6);
    const dest = `${r.net}/${r.prefix}`;
    if (r.code === 'C' || r.code === 'L') return `${code}${dest} is directly connected, ${r.ifc}`;
    if (r.exitOnly) return `${code}${dest} is directly connected, ${r.ifc}`;
    return `${code}${dest} ${tail(r)}`;
  };
  // Equal-cost paths: first path on the prefix line, the others indented below it (IOS style).
  const emit = (list, indent) => {
    const seen = {};
    for (const r of list) {
      const k = `${r.net}/${r.prefix}/${r.code}`;
      if (seen[k] !== undefined && r.nh) { out.push(`${' '.repeat(seen[k])}${tail(r)}`); continue; }
      const l = line(r, indent);
      seen[k] = l.indexOf('[');
      out.push(l);
    }
  };
  if (rt.some((r) => r.prefix === 0)) emit(rt.filter((r) => r.prefix === 0), false);
  // Group by classful major network, like IOS.
  const groups = {};
  for (const r of rt.filter((x) => x.prefix !== 0)) {
    const cp = classfulPrefix(r.net);
    const major = `${networkOf(r.net, cp)}/${cp}`;
    (groups[major] = groups[major] || []).push(r);
  }
  const majors = Object.keys(groups).sort((a, b) => {
    const [x] = a.split('/'), [y] = b.split('/');
    return x.split('.').map(Number).reduce((s, v) => s * 256 + v, 0) - y.split('.').map(Number).reduce((s, v) => s * 256 + v, 0);
  });
  for (const m of majors) {
    const g = groups[m].sort((a, b) => (a.net === b.net ? a.prefix - b.prefix : a.net.localeCompare(b.net, undefined, { numeric: true })));
    const uniq = new Set(g.map((r) => `${r.net}/${r.prefix}/${r.code === 'L' ? 'L' : ''}`)).size;
    if (uniq === 1 && `${g[0].net}/${g[0].prefix}` === m) { emit(g, false); continue; }
    const masks = new Set(g.map((r) => r.prefix));
    out.push(masks.size > 1
      ? `      ${m} is variably subnetted, ${uniq} subnets, ${masks.size} masks`
      : `      ${m.split('/')[0]}/${[...masks][0]} is subnetted, ${uniq} subnets`);
    emit(g, true);
  }
  return out;
}

export function ipArp(net, id) {
  const d = dev(net, id);
  const out = ['Protocol  Address          Age (min)  Hardware Addr   Type   Interface'];
  for (const [n, i] of Object.entries(d.interfaces).sort(([a], [b]) => ifSort(a, b))) {
    if (i.ip && !/^(Serial|Tunnel|Loopback)/.test(n) && ifStatus(net, id, n).protocol === 'up') out.push(`Internet  ${pad(i.ip.ip, 17)}${pad('-', 11)}${macCisco(i.mac)}  ARPA   ${n}`);
  }
  for (const [ip, e] of Object.entries(d.arp)) out.push(`Internet  ${pad(ip, 17)}${pad('0', 11)}${macCisco(e.mac)}  ARPA   ${e.ifc}`);
  return out;
}

export function cdpNeighbors(net, id) {
  const d = dev(net, id);
  const out = [
    'Capability Codes: R - Router, T - Trans Bridge, B - Source Route Bridge',
    '                  S - Switch, H - Host, I - IGMP, r - Repeater, P - Phone',
    '',
    'Device ID    Local Intrfce   Holdtme    Capability   Platform    Port ID',
  ];
  for (const n of sortedIfs(d)) {
    const l = linkOf(net, id, n);
    if (!l || !ifStatus(net, id, n).connected) continue;
    const p = peerOf(l, id, n);
    const pd = dev(net, p.dev);
    if (isHost(pd)) continue; // Windows hosts do not speak CDP
    if (!cdpOn(d, n) || !cdpOn(pd, p.ifc)) continue; // "no cdp run" / "no cdp enable" on either side
    const cap = pd.kind === 'router' ? 'R' : 'S';
    const plat = pd.kind === 'router' ? 'C2900' : '2960';
    out.push(`${pad(pd.hostname, 13)}${pad(cdpIf(n), 16)}${pad('157', 15)}${pad(cap, 13)}${pad(plat, 12)}${cdpIf(p.ifc)}`);
  }
  return out;
}

export function natTranslations(net, id) {
  const d = dev(net, id);
  const out = ['Pro  Inside global         Inside local          Outside local         Outside global'];
  for (const st of d.nat.statics || []) out.push(`---  ${pad(st.global, 22)}${pad(st.local, 22)}${pad('---', 22)}---`);
  for (const [l, g] of Object.entries(d.nat.natBindings || {})) out.push(`---  ${pad(g, 22)}${pad(l, 22)}${pad('---', 22)}---`);
  for (const t of d.nat.translations.slice(-12)) {
    const port = t.lport ?? (t.proto === 'icmp' ? 1 : 49152 + (ipToInt(t.insideLocal) & 1023));
    out.push(`${pad(t.proto, 5)}${pad(`${t.insideGlobal}:${port}`, 22)}${pad(`${t.insideLocal}:${port}`, 22)}${pad(`${t.outside}:${t.port || port}`, 22)}${t.outside}:${t.port || port}`);
  }
  return out;
}

export function dhcpBinding(net, id) {
  const d = dev(net, id);
  const out = ['IP address       Client-ID/              Lease expiration        Type', '                 Hardware address'];
  for (const b of d.dhcp.bindings) out.push(`${pad(b.ip, 17)}${pad(macCisco(b.mac), 24)}${pad('Oct 01 2026 09:14 AM', 24)}Automatic`);
  return out;
}

export function ipv6IntBrief(net, id) {
  const d = dev(net, id);
  const out = [];
  for (const n of sortedIfs(d)) {
    const i = d.interfaces[n];
    const st = ifStatus(net, id, n);
    out.push(`${pad(n, 23)}[${st.status === 'administratively down' ? 'administratively down/down' : `${st.status}/${st.protocol}`}]`);
    if (i.ipv6Enabled || i.ipv6.length) {
      const ll = i.ipv6.find((a) => a.addr.startsWith('fe80'))?.addr;
      out.push(`    ${(ll || linkLocalOf(i)).toUpperCase()}`);
      for (const a of i.ipv6.filter((x) => !x.addr.startsWith('fe80'))) out.push(`    ${v6Compress(a.addr).toUpperCase()}`);
    } else out.push('    unassigned');
  }
  return out;
}
import { linkLocalFromMac } from '../net/addr.js';
const linkLocalOf = (i) => linkLocalFromMac(i.mac);

const uptimeText = (net, d) => {
  if (!d.sys?.reloads || !net) return '3 weeks, 2 days, 4 hours, 17 minutes';
  const min = Math.floor((((net.ticks || 0) - (d.sys.bootedAt || 0)) * 7) / 60);
  return min < 60 ? `${min} minute${min === 1 ? '' : 's'}` : `${Math.floor(min / 60)} hour${min >= 120 ? 's' : ''}, ${min % 60} minutes`;
};
export function version(d, net) {
  if (d.sys) return sysVersion(d, net);
  if (d.kind === 'switch') {
    return [
      'Cisco IOS Software, C2960 Software (C2960-LANBASEK9-M), Version 15.0(2)SE4, RELEASE SOFTWARE (fc1)',
      'Technical Support: http://www.cisco.com/techsupport',
      'Copyright (c) 1986-2013 by Cisco Systems, Inc.',
      '',
      'ROM: Bootstrap program is C2960 boot loader',
      `${d.hostname} uptime is 3 weeks, 2 days, 4 hours, 17 minutes`,
      'System image file is "flash:c2960-lanbasek9-mz.150-2.SE4.bin"',
      '',
      'cisco WS-C2960-24TT-L (PowerPC405) processor (revision B0) with 65536K bytes of memory.',
      '24 FastEthernet interfaces',
      '2 Gigabit Ethernet interfaces',
      '',
      `Base ethernet MAC Address       : ${d.baseMac.match(/../g).join(':').toUpperCase()}`,
      'Model number                    : WS-C2960-24TT-L',
      '',
      'Configuration register is 0xF',
    ];
  }
  return [
    'Cisco IOS Software, C2900 Software (C2900-UNIVERSALK9-M), Version 15.7(3)M8, RELEASE SOFTWARE (fc2)',
    'Technical Support: http://www.cisco.com/techsupport',
    'Copyright (c) 1986-2021 by Cisco Systems, Inc.',
    '',
    'ROM: System Bootstrap, Version 15.0(1r)M15, RELEASE SOFTWARE (fc1)',
    `${d.hostname} uptime is 3 weeks, 2 days, 4 hours, 17 minutes`,
    'System image file is "flash0:c2900-universalk9-mz.SPA.157-3.M8.bin"',
    '',
    'Cisco CISCO2911/K9 (revision 1.0) with 491520K/32768K bytes of memory.',
    `${Object.keys(d.interfaces).length} Gigabit Ethernet interfaces`,
    '255K bytes of non-volatile configuration memory.',
    '',
    'Configuration register is 0x2102',
  ];
}

function sysVersion(d, net) {
  const s = d.sys;
  const sw = d.kind === 'switch';
  const fs = sw ? 'flash' : 'flash0';
  const reg = s.confreg || '0xF';
  return [
    sw ? `Cisco IOS Software, C2960 Software (C2960-LANBASEK9-M), Version ${s.image.version}, RELEASE SOFTWARE (fc1)` : `Cisco IOS Software, C2900 Software (C2900-UNIVERSALK9-M), Version ${s.image.version}, RELEASE SOFTWARE (fc2)`,
    'Technical Support: http://www.cisco.com/techsupport',
    'Copyright (c) 1986-2021 by Cisco Systems, Inc.',
    '',
    sw ? 'ROM: Bootstrap program is C2960 boot loader' : 'ROM: System Bootstrap, Version 15.0(1r)M15, RELEASE SOFTWARE (fc1)',
    '',
    `${d.hostname} uptime is ${uptimeText(net, d)}`,
    `System returned to ROM by ${s.reloads ? 'reload' : 'power-on'}`,
    `System image file is "${fs}:${s.image.file}"`,
    '',
    sw ? 'cisco WS-C2960-24TT-L (PowerPC405) processor (revision B0) with 65536K bytes of memory.' : 'Cisco CISCO2911/K9 (revision 1.0) with 491520K/32768K bytes of memory.',
    `Processor board ID ${d.serial || (sw ? 'FOC1932X0AB' : 'FTX1840A1BC')}`,
    `${Object.keys(d.interfaces).filter((n) => /Ethernet/.test(n)).length} Ethernet interfaces`,
    `${sw ? '64K' : '255K'} bytes of non-volatile configuration memory.`,
    `${Math.round(s.flash.size / 1024)}K bytes of ${sw ? 'flash-simulated non-volatile configuration memory' : 'ATA System CompactFlash 0 (Read/Write)'}.`,
    '',
    `Configuration register is ${reg}${s.confregNext ? ` (will be ${s.confregNext} at next reload)` : ''}`,
  ];
}

// ------------------------------------------------------------- running-config
export function runningConfig(net, id, onlyIf) {
  const d = dev(net, id);
  const out = [];
  const ifBlock = (n, o = out) => {
    const i = d.interfaces[n];
    const out = o;
    out.push(`interface ${n}`);
    if (i.description) out.push(` description ${i.description}`);
    if (d.kind === 'switch') {
      out.push(...switchIfLines(d, n, i));
      out.push(...secIfLines(d, n, i));
    } else {
      if (i.encap) out.push(` encapsulation dot1Q ${i.encap.vlan}${i.encap.native ? ' native' : ''}`);
      if (i.ip) out.push(` ip address ${i.ip.ip} ${i.ip.mask}`); else out.push(' no ip address');
      if (i.nat) out.push(` ip nat ${i.nat}`);
      if (i.helper) out.push(` ip helper-address ${i.helper}`);
      out.push(...routingIfLines(n, i));
      out.push(...wanIfLines(n, i));
      out.push(...svcIfLines(n, i));
      out.push(...secIfLines(d, n, i));
      for (const a of i.ipv6) out.push(` ipv6 address ${a.addr.toUpperCase()}${a.addr.startsWith('fe80') ? ' link-local' : `/${a.prefix}`}`);
      if (i.ipv6Enabled && !i.ipv6.length) out.push(' ipv6 enable');
      if (!isSubif(n) && !/^(Loopback|Serial|Tunnel)/.test(n)) out.push(` duplex ${i.duplex}`, ` speed ${i.speed}`);
    }
    if (d.kind === 'switch' && !isSvi(n) && i.speed !== 'auto') out.push(` speed ${i.speed}`);
    if (d.kind === 'switch' && !isSvi(n) && i.duplex !== 'auto') out.push(` duplex ${i.duplex}`);
    out.push(...mgmtIfLines(d, n, i));
    if (i.shutdown) out.push(' shutdown');
    out.push('!');
  };
  if (onlyIf) {
    out.push('Building configuration...', '', `Current configuration : ${120 + onlyIf.length} bytes`, '!');
    ifBlock(onlyIf);
    out.push('end');
    return out;
  }
  out.push('Building configuration...', '');
  const body = [];
  const push = (...l) => body.push(...l);
  push('!', `version ${d.kind === 'switch' ? '15.0' : '15.7'}`, d.timestamps ? 'service timestamps log datetime msec' : 'no service timestamps log datetime msec',
    'no service timestamps debug datetime msec', d.mgmt?.encrypt ? 'service password-encryption' : 'no service password-encryption', '!', `hostname ${d.hostname}`, '!');
  push(...mgmtGlobalLines(d));
  push(...secGlobalLines(d));
  if (d.kind === 'router' && svcConfigLines(d).length) push(...svcConfigLines(d), '!');
  if (!d.domainLookup) push('no ip domain-lookup', '!');
  if (d.kind === 'router') {
    for (const [a, b] of d.dhcp.excluded) push(`ip dhcp excluded-address ${a}${b ? ` ${b}` : ''}`);
    for (const p of Object.values(d.dhcp.pools)) {
      push(`ip dhcp pool ${p.name}`);
      if (p.network) push(` network ${p.network} ${p.mask}`);
      if (p.router) push(` default-router ${p.router}`);
      if (p.dns?.length) push(` dns-server ${p.dns.join(' ')}`);
      if (p.domain) push(` domain-name ${p.domain}`);
      if (p.leaseDays != null) push(` lease ${p.leaseDays}`);
    }
    if (Object.keys(d.dhcp.pools).length) push('!');
    if (d.ipv6Routing) push('ipv6 unicast-routing', '!');
    push(...wanConfigLines(d));
  }
  if (d.kind === 'switch') push(...switchGlobalLines(d));
  for (const n of Object.keys(d.interfaces).sort(ifSort)) ifBlock(n, body);
  if (d.kind === 'switch' && !d.interfaces.Vlan1) push('interface Vlan1', ' no ip address', ' shutdown', '!');
  if (d.kind === 'switch') {
    if (d.defaultGateway) push(`ip default-gateway ${d.defaultGateway}`);
    for (const r of d.routes || []) push(`ip route ${r.net} ${r.mask} ${r.nh || r.ifc}`);
    if (d.defaultGateway || d.routes?.length) push('!');
  }
  if (d.kind === 'router') push(...routingConfigLines(d));
  if (d.kind === 'router') {
    for (const r of d.nat.rules) if (r.ifc) push(`ip nat inside source list ${r.acl} interface ${r.ifc} overload`);
    push(...svcTailLines(d));
    push('ip classless');
    for (const r of d.routes) push(`ip route ${r.net} ${r.mask} ${r.nh || r.ifc}${r.ad && r.ad !== 1 ? ` ${r.ad}` : ''}`);
    push('!', 'ip flow-export version 9', '!');
  }
  const numbered = secNumberedAclLines(d);
  if (numbered.length) push(...numbered, '!');
  push(...secTailLines(d));
  push(...mgmtTailLines(d));
  const lineBlocks = secLineBlocks(d);
  if (lineBlocks) push(...lineBlocks, 'end');
  else push('line con 0', '!', 'line aux 0', '!', 'line vty 0 4', ' login', '!', 'end');
  const bytes = body.join('\n').length;
  out.push(`Current configuration : ${bytes} bytes`, ...body);
  return out;
}
export { prefixToMask };
